// Bank Statements tab — Phase 3 of the Bank Statement Upload feature.
// Mounted from src/pages/pos/Purchases.jsx as a sibling to the existing
// Purchase Orders / Invoice Upload / Quick Entry / Spend Analytics tabs.
// Self-contained: all state, child sections, and effects live here so
// Purchases.jsx only needs an import + tab button + render line.

import { useEffect, useMemo, useRef, useState } from 'react'
import * as Sentry from '@sentry/react'
import {
  Plus, Upload, Trash2, Edit2, Check, X, Filter, ChevronDown, ChevronRight,
  Loader, Banknote, AlertTriangle, CheckCircle, Tag, Lock,
} from 'lucide-react'
import { useToast } from '../../context/ToastContext'
import {
  getBankTransactions, refreshBankTransactions, saveBankTransactionsBatch,
  updateBankTransaction, deleteBankTransaction,
  getBankCharges, refreshBankCharges, saveBankChargesBatch,
  getExpenseCategories, refreshExpenseCategories, saveExpenseCategory,
  deleteExpenseCategory, seedDefaultExpenseCategories,
} from '../../lib/bankStore'
import { parseBankStatementFile } from '../../lib/bankStatementParser'

const BANK_OPTIONS = ['FNB', 'Standard Bank', 'Absa', 'Nedbank', 'Capitec', 'Other']

const COLOUR_SWATCHES = [
  '#10b981', '#3b82f6', '#a855f7', '#f59e0b',
  '#06b6d4', '#ef4444', '#ec4899', '#14b8a6',
]

const HIDDEN_INPUT_STYLE = {
  position: 'absolute', width: '1px', height: '1px',
  opacity: 0, overflow: 'hidden', pointerEvents: 'none',
}

function formatR(n) {
  const num = Number(n || 0)
  return `R${num.toFixed(2)}`
}

function todayISO() { return new Date().toISOString().slice(0, 10) }

function firstOfMonthISO() {
  const d = new Date()
  d.setDate(1)
  return d.toISOString().slice(0, 10)
}

// Description → 1-3 keyword tokens, used for self-learning categories.
function descriptionKeywords(description) {
  return String(description || '')
    .toLowerCase()
    .replace(/\d+/g, '')
    .replace(/[^a-z\s]/g, '')
    .trim()
    .split(/\s+/)
    .filter(w => w.length > 2)
    .slice(0, 3)
}

const CHARGE_TYPE_LABELS = {
  service_fee: 'Service Fees',
  cash_deposit_fee: 'Cash Deposit Fees',
  cash_handling_fee: 'Cash Handling Fees',
  other_fee: 'Other Fees',
}

export default function BankStatementsTab({ departmentId }) {
  const { addToast } = useToast()
  const [refreshKey, setRefreshKey] = useState(0)
  const bump = () => setRefreshKey(k => k + 1)

  // Refresh from Firestore on mount + when dept changes
  useEffect(() => {
    let cancelled = false
    async function init() {
      try {
        await seedDefaultExpenseCategories(departmentId)
        await Promise.all([
          refreshBankTransactions(departmentId),
          refreshBankCharges(departmentId),
          refreshExpenseCategories(departmentId),
        ])
      } catch (err) {
        console.warn('[BankStatementsTab] init refresh failed:', err?.message)
      }
      if (!cancelled) bump()
    }
    init()
    return () => { cancelled = true }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departmentId])

  // Synchronous reads from localStorage cache
  const transactions = useMemo(() => getBankTransactions(departmentId), [departmentId, refreshKey])
  const charges = useMemo(() => getBankCharges(departmentId), [departmentId, refreshKey])
  const categories = useMemo(() => getExpenseCategories(departmentId), [departmentId, refreshKey])

  // Account list is derived from transactions — every imported
  // statement reveals an account, so we no longer need to manage
  // them manually in a separate "Linked Accounts" section.
  const knownAccounts = useMemo(() => {
    const map = new Map()
    for (const t of transactions) {
      if (!t.accountNumber) continue
      if (!map.has(t.accountNumber)) {
        map.set(t.accountNumber, {
          accountNumber: t.accountNumber,
          bankName: t.bankName || '',
        })
      }
    }
    return Array.from(map.values())
  }, [transactions])

  return (
    <div className="space-y-6">
      <UploadStatementSection
        departmentId={departmentId}
        categories={categories}
        addToast={addToast}
        onImported={bump}
      />

      <TransactionsSection
        departmentId={departmentId}
        transactions={transactions}
        accounts={knownAccounts}
        categories={categories}
        addToast={addToast}
        onChange={bump}
      />

      <BankChargesSummarySection
        charges={charges}
        transactions={transactions}
      />

      <CategoriesPanel
        departmentId={departmentId}
        categories={categories}
        transactions={transactions}
        addToast={addToast}
        onChange={bump}
      />
    </div>
  )
}

// ─── Section 1: Upload Statement ──────────────────────────────────

function UploadStatementSection({ departmentId, categories, addToast, onImported }) {
  const fileRef = useRef(null)
  const [parsing, setParsing] = useState(false)
  const [parseProgress, setParseProgress] = useState('')
  const [parseError, setParseError] = useState('')
  const [parsed, setParsed] = useState(null) // canonical bank statement output
  const [previewRows, setPreviewRows] = useState([]) // editable copy
  const [filterMode, setFilterMode] = useState('all')
  const [importing, setImporting] = useState(false)
  // Optional bank hint — passed through to parseBankStatementFile.
  // Empty string means "auto-detect" (current default).
  const [bankHint, setBankHint] = useState('')
  // Read-only summary card shown after a successful import. Carries
  // bank name, masked account number, statement period and totals.
  const [lastImport, setLastImport] = useState(null)

  function reset() {
    setParsed(null)
    setPreviewRows([])
    setParseError('')
    setParseProgress('')
    setFilterMode('all')
    if (fileRef.current) fileRef.current.value = ''
  }

  async function handleFile(file) {
    if (!file) return
    reset()
    setLastImport(null)
    setParsing(true)
    try {
      const result = await parseBankStatementFile(file, {
        bankName: bankHint || undefined,
        onProgress: msg => setParseProgress(msg),
      })
      setParsed(result)
      const rows = [
        ...result.creditTransactions.map(t => ({ ...t, type: 'credit', _key: `c_${t.date}_${t.description}_${t.amount}` })),
        ...result.debitTransactions.map(t => ({ ...t, type: 'debit', _key: `d_${t.date}_${t.description}_${t.amount}` })),
      ]
      // Sort by date asc then by type (credits first)
      rows.sort((a, b) => a.date.localeCompare(b.date) || (a.type === 'credit' ? -1 : 1))
      setPreviewRows(rows)
      if (result.parseErrors?.length > 0) {
        addToast(`Parsed with warnings: ${result.parseErrors.join('; ')}`, 'warning')
      } else {
        addToast(`Parsed ${rows.length} transactions`, 'success')
      }
    } catch (err) {
      console.error('[BankStatementsTab] parse failed:', err)
      setParseError(err.message || 'Failed to parse statement')
      Sentry.captureException(err, { extra: { fn: 'BankStatementsTab.parseStatement', departmentId } })
    } finally {
      setParsing(false)
    }
  }

  function updateRow(key, updates) {
    setPreviewRows(rows => rows.map(r => r._key === key ? { ...r, ...updates } : r))
  }

  function removeRow(key) {
    setPreviewRows(rows => rows.filter(r => r._key !== key))
  }

  const filteredRows = useMemo(() => {
    switch (filterMode) {
      case 'debits': return previewRows.filter(r => r.type === 'debit')
      case 'credits': return previewRows.filter(r => r.type === 'credit')
      case 'uncategorised': return previewRows.filter(r => r.type === 'debit' && (!r.category || r.category === 'Other'))
      default: return previewRows
    }
  }, [previewRows, filterMode])

  const summary = useMemo(() => {
    let credits = 0, debits = 0, uncategorised = 0
    for (const r of previewRows) {
      if (r.type === 'credit') credits += r.amount
      else {
        debits += r.amount
        if (!r.category || r.category === 'Other') uncategorised++
      }
    }
    return { credits, debits, uncategorised }
  }, [previewRows])

  async function handleImport() {
    if (!parsed || previewRows.length === 0) return
    setImporting(true)
    try {
      const acctNumber = parsed.accountNumber
      const stmtPeriod = parsed.statementPeriod?.from?.slice(0, 7) || ''

      const txs = previewRows.map(r => ({
        departmentId,
        accountNumber: acctNumber,
        bankName: parsed.bankName,
        date: r.date,
        description: r.description,
        amount: r.amount,
        type: r.type,
        category: r.category || 'Other',
        confirmed: !!r.confirmed,
        source: 'statement_upload',
        statementPeriod: stmtPeriod,
      }))

      const txResult = await saveBankTransactionsBatch(txs)

      // Save accrued bank charges as charge records, plus the four
      // summary totals from the charges section.
      const yearMonth = stmtPeriod
      const summaryCharges = []
      const c = parsed.bankCharges || {}
      if (c.serviceFees > 0) summaryCharges.push({ chargeType: 'service_fee', amount: c.serviceFees })
      if (c.cashDepositFees > 0) summaryCharges.push({ chargeType: 'cash_deposit_fee', amount: c.cashDepositFees })
      if (c.cashHandlingFees > 0) summaryCharges.push({ chargeType: 'cash_handling_fee', amount: c.cashHandlingFees })
      if (c.otherFees > 0) summaryCharges.push({ chargeType: 'other_fee', amount: c.otherFees })

      const chargesPayload = summaryCharges.map(sc => ({
        departmentId,
        accountNumber: acctNumber,
        bankName: parsed.bankName,
        yearMonth,
        chargeType: sc.chargeType,
        amount: sc.amount,
        statementDate: parsed.statementPeriod?.to || '',
      }))

      let chargeResult = { saved: 0, failed: 0 }
      if (chargesPayload.length > 0) {
        chargeResult = await saveBankChargesBatch(chargesPayload)
      }

      addToast(
        `Imported ${txResult.saved} transactions, ${chargeResult.saved} charge totals` +
        (txResult.failed ? ` (${txResult.failed} failed)` : ''),
        txResult.failed ? 'warning' : 'success'
      )
      setLastImport({
        bankName: parsed.bankName,
        accountNumber: acctNumber,
        statementPeriod: parsed.statementPeriod,
        savedTx: txResult.saved,
        savedCharges: chargeResult.saved,
      })
      reset()
      onImported()
    } catch (err) {
      console.error('[BankStatementsTab] import failed:', err)
      addToast(`Import failed: ${err.message}`, 'error')
      Sentry.captureException(err, { extra: { fn: 'BankStatementsTab.handleImport', departmentId } })
    } finally {
      setImporting(false)
    }
  }

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-lg p-5">
      <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-1">
        <Upload size={18} className="text-brand-400" /> Upload Statement
      </h2>
      <p className="text-xs text-gray-500 mb-4">
        Upload a bank statement PDF. The bank name and account number
        are extracted automatically — no setup needed.
      </p>

      <div className="space-y-3">
        <div>
          <label className="block text-xs text-gray-400 mb-1.5">
            Which bank is this statement from? <span className="text-gray-600">(optional)</span>
          </label>
          <div className="flex flex-wrap gap-1.5">
            {[
              { key: '', label: 'Auto-detect' },
              ...BANK_OPTIONS.map(b => ({ key: b, label: b })),
            ].map(opt => (
              <button
                key={opt.key || 'auto'}
                type="button"
                onClick={() => setBankHint(opt.key)}
                disabled={parsing || importing}
                className={`px-3 py-1.5 text-xs rounded border transition ${
                  bankHint === opt.key
                    ? 'bg-brand-600 border-brand-500 text-white'
                    : 'bg-gray-900 border-gray-700 text-gray-300 hover:border-gray-600'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={parsing || importing}
            className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-white px-4 py-2 rounded text-sm"
          >
            <Upload size={14} /> Choose PDF
          </button>
          <input
            ref={fileRef}
            type="file"
            accept=".pdf,application/pdf,application/x-pdf"
            onChange={e => handleFile(e.target.files?.[0])}
            style={HIDDEN_INPUT_STYLE}
          />
          {parsing && (
            <span className="flex items-center gap-2 text-sm text-gray-400">
              <Loader size={13} className="animate-spin" /> {parseProgress || 'Reading statement...'}
            </span>
          )}
        </div>
      </div>

      {lastImport && !parsed && (
        <div className="mt-4 bg-green-950/30 border border-green-800/50 rounded p-3 text-xs text-green-200">
          <div className="flex items-center gap-2 font-medium mb-1">
            <CheckCircle size={13} className="text-green-400" />
            Last import: {lastImport.savedTx} transaction{lastImport.savedTx !== 1 ? 's' : ''}
            {lastImport.savedCharges > 0 && `, ${lastImport.savedCharges} charge total${lastImport.savedCharges !== 1 ? 's' : ''}`}
          </div>
          <div className="text-green-300/80">
            <strong className="text-green-200">{lastImport.bankName}</strong> ****{lastImport.accountNumber}
            {lastImport.statementPeriod?.from && (
              <span> · Period: {lastImport.statementPeriod.from} → {lastImport.statementPeriod.to}</span>
            )}
          </div>
        </div>
      )}

      {parseError && (
        <div className="bg-red-950/40 border border-red-800/50 rounded px-4 py-3 mt-3 flex items-start gap-2">
          <AlertTriangle size={14} className="text-red-400 shrink-0 mt-0.5" />
          <p className="text-sm text-red-300">{parseError}</p>
        </div>
      )}

      {parsed && previewRows.length > 0 && (
        <div className="mt-4 space-y-3">
          <div className="bg-gray-800/40 border border-gray-700 rounded px-4 py-2 text-xs text-gray-400 flex items-center gap-4 flex-wrap">
            <span><strong className="text-gray-200">{parsed.bankName}</strong> ****{parsed.accountNumber}</span>
            {parsed.statementPeriod?.from && (
              <span>Period: {parsed.statementPeriod.from} → {parsed.statementPeriod.to}</span>
            )}
            {parsed.openingBalance != null && <span>Opening: {formatR(parsed.openingBalance)}</span>}
            {parsed.closingBalance != null && <span>Closing: {formatR(parsed.closingBalance)}</span>}
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1.5 flex-wrap">
            {[
              { key: 'all', label: `All (${previewRows.length})` },
              { key: 'debits', label: `Debits (${previewRows.filter(r => r.type === 'debit').length})` },
              { key: 'credits', label: `Credits (${previewRows.filter(r => r.type === 'credit').length})` },
              { key: 'uncategorised', label: `Uncategorised (${summary.uncategorised})` },
            ].map(opt => (
              <button
                key={opt.key}
                onClick={() => setFilterMode(opt.key)}
                className={`px-2.5 py-1 text-xs rounded border transition ${
                  filterMode === opt.key
                    ? 'bg-brand-600 border-brand-500 text-white'
                    : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>

          <div className="overflow-x-auto border border-gray-800 rounded max-h-96">
            <table className="w-full text-xs">
              <thead className="bg-gray-800/60 text-gray-400 sticky top-0">
                <tr>
                  <th className="text-left py-2 px-2">Date</th>
                  <th className="text-left py-2 px-2">Description</th>
                  <th className="text-right py-2 px-2">Amount</th>
                  <th className="text-left py-2 px-2">Type</th>
                  <th className="text-left py-2 px-2">Category</th>
                  <th className="text-center py-2 px-2 w-12"></th>
                </tr>
              </thead>
              <tbody>
                {filteredRows.map(row => {
                  const uncategorised = row.type === 'debit' && (!row.category || row.category === 'Other')
                  return (
                    <tr key={row._key} className="border-t border-gray-800/60">
                      <td className="py-1.5 px-2 text-gray-300 font-mono">{row.date}</td>
                      <td className="py-1.5 px-2 text-gray-300">{row.description}</td>
                      <td className={`py-1.5 px-2 text-right font-medium ${row.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                        {row.type === 'credit' ? '+' : '-'}{formatR(row.amount)}
                      </td>
                      <td className="py-1.5 px-2">
                        {row.type === 'credit' ? (
                          <span className="text-green-400 text-xs">Credit</span>
                        ) : (
                          <span className="text-red-400 text-xs">Debit</span>
                        )}
                      </td>
                      <td className={`py-1.5 px-2 ${uncategorised ? 'bg-amber-950/30' : ''}`}>
                        {row.type === 'credit' ? (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
                            <Lock size={10} /> Sales
                          </span>
                        ) : (
                          <select
                            value={row.category || 'Other'}
                            onChange={e => updateRow(row._key, { category: e.target.value })}
                            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white"
                          >
                            {categories.length === 0 && <option value="Other">Other</option>}
                            {categories.map(c => (
                              <option key={c.id} value={c.name}>{c.name}</option>
                            ))}
                          </select>
                        )}
                      </td>
                      <td className="py-1.5 px-2 text-center">
                        <button onClick={() => removeRow(row._key)} className="text-gray-500 hover:text-red-400">
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between flex-wrap gap-3 text-xs">
            <div className="text-gray-400">
              Credits: <span className="text-green-400 font-medium">{formatR(summary.credits)}</span>
              {' | '}Debits: <span className="text-red-400 font-medium">{formatR(summary.debits)}</span>
              {' | '}Bank Charges: <span className="text-amber-400 font-medium">{formatR(
                (parsed.bankCharges?.serviceFees || 0) +
                (parsed.bankCharges?.cashDepositFees || 0) +
                (parsed.bankCharges?.cashHandlingFees || 0) +
                (parsed.bankCharges?.otherFees || 0)
              )}</span>
              {summary.uncategorised > 0 && (
                <> {' | '}<span className="text-amber-400">{summary.uncategorised} uncategorised</span></>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={reset} className="px-3 py-1.5 text-sm text-gray-400 hover:text-white">Discard</button>
              <button
                onClick={handleImport}
                disabled={importing || previewRows.length === 0}
                className="flex items-center gap-1.5 bg-brand-600 hover:bg-brand-500 disabled:opacity-50 text-white px-4 py-2 rounded text-sm"
              >
                {importing ? <Loader size={12} className="animate-spin" /> : <Upload size={12} />}
                Import {previewRows.length} transactions
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}

// ─── Section 3: Transactions ──────────────────────────────────────

function TransactionsSection({ departmentId, transactions, accounts, categories, addToast, onChange }) {
  const [dateFrom, setDateFrom] = useState(firstOfMonthISO())
  const [dateTo, setDateTo] = useState(todayISO())
  const [filterCategory, setFilterCategory] = useState('')
  const [filterType, setFilterType] = useState('')
  const [filterAccount, setFilterAccount] = useState('')
  const [filterConfirmed, setFilterConfirmed] = useState('')
  const [editingId, setEditingId] = useState(null)
  const [editForm, setEditForm] = useState({})
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [selected, setSelected] = useState(new Set())
  const [bulkCategory, setBulkCategory] = useState('')

  const filtered = useMemo(() => {
    let list = transactions
    if (dateFrom) list = list.filter(t => t.date >= dateFrom)
    if (dateTo) list = list.filter(t => t.date <= dateTo)
    if (filterCategory) list = list.filter(t => t.category === filterCategory)
    if (filterType) list = list.filter(t => t.type === filterType)
    if (filterAccount) list = list.filter(t => t.accountNumber === filterAccount)
    if (filterConfirmed === 'confirmed') list = list.filter(t => t.confirmed)
    if (filterConfirmed === 'unconfirmed') list = list.filter(t => !t.confirmed)
    return [...list].sort((a, b) => b.date.localeCompare(a.date))
  }, [transactions, dateFrom, dateTo, filterCategory, filterType, filterAccount, filterConfirmed])

  function startEdit(tx) {
    setEditingId(tx.id)
    setEditForm({ description: tx.description, category: tx.category, notes: tx.notes || '' })
  }

  // Self-learning: when a debit's category is corrected, append the
  // top tokens from its description to the category's keyword list.
  async function teachCategory(category, description) {
    const cat = categories.find(c => c.name === category)
    if (!cat) return
    const tokens = descriptionKeywords(description)
    if (tokens.length === 0) return
    const existing = new Set((cat.keywords || []).map(k => String(k).toLowerCase()))
    let added = false
    for (const t of tokens) {
      if (!existing.has(t)) { existing.add(t); added = true }
    }
    if (!added) return
    try {
      await saveExpenseCategory({
        departmentId,
        name: cat.name,
        type: cat.type,
        colour: cat.colour,
        keywords: Array.from(existing),
      })
    } catch (err) {
      console.warn('[BankStatementsTab] category teach failed:', err?.message)
    }
  }

  async function saveEdit(tx) {
    try {
      const updated = await updateBankTransaction(tx.id, departmentId, {
        description: editForm.description,
        category: editForm.category,
        notes: editForm.notes,
      })
      if (updated && tx.type === 'debit' && editForm.category && editForm.category !== tx.category) {
        await teachCategory(editForm.category, editForm.description || tx.description)
      }
      addToast('Transaction updated', 'success')
      setEditingId(null)
      onChange()
    } catch (err) {
      addToast(`Failed to update: ${err.message}`, 'error')
    }
  }

  async function handleDelete(id) {
    try {
      await deleteBankTransaction(id, departmentId)
      addToast('Transaction deleted', 'success')
      setConfirmDelete(null)
      onChange()
    } catch (err) {
      addToast(`Failed to delete: ${err.message}`, 'error')
    }
  }

  async function toggleConfirm(tx) {
    try {
      await updateBankTransaction(tx.id, departmentId, { confirmed: !tx.confirmed })
      onChange()
    } catch (err) {
      addToast(`Failed to update: ${err.message}`, 'error')
    }
  }

  function toggleSelect(id) {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  function selectAllVisible() {
    setSelected(new Set(filtered.map(t => t.id)))
  }

  function clearSelection() { setSelected(new Set()) }

  async function bulkAssignCategory() {
    if (!bulkCategory || selected.size === 0) return
    let ok = 0, fail = 0
    for (const id of selected) {
      try {
        const tx = transactions.find(t => t.id === id)
        if (!tx) continue
        await updateBankTransaction(id, departmentId, { category: bulkCategory })
        if (tx.type === 'debit') await teachCategory(bulkCategory, tx.description)
        ok++
      } catch { fail++ }
    }
    addToast(`Updated ${ok}${fail ? `, ${fail} failed` : ''}`, fail ? 'warning' : 'success')
    setBulkCategory('')
    clearSelection()
    onChange()
  }

  async function bulkDelete() {
    let ok = 0, fail = 0
    for (const id of selected) {
      try { await deleteBankTransaction(id, departmentId); ok++ }
      catch { fail++ }
    }
    addToast(`Deleted ${ok}${fail ? `, ${fail} failed` : ''}`, fail ? 'warning' : 'success')
    clearSelection()
    onChange()
  }

  async function bulkConfirm() {
    let ok = 0, fail = 0
    for (const id of selected) {
      try { await updateBankTransaction(id, departmentId, { confirmed: true }); ok++ }
      catch { fail++ }
    }
    addToast(`Confirmed ${ok}${fail ? `, ${fail} failed` : ''}`, fail ? 'warning' : 'success')
    clearSelection()
    onChange()
  }

  const categoryColour = (name) => categories.find(c => c.name === name)?.colour || '#9ca3af'

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-lg p-5">
      <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
        <Filter size={18} className="text-brand-400" /> Transactions
      </h2>

      {/* Filters */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-4">
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">From</label>
          <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white" />
        </div>
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">To</label>
          <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white" />
        </div>
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">Category</label>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white">
            <option value="">All</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
            <option value="Sales">Sales</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">Type</label>
          <select value={filterType} onChange={e => setFilterType(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white">
            <option value="">All</option>
            <option value="debit">Debits</option>
            <option value="credit">Credits</option>
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">Account</label>
          <select value={filterAccount} onChange={e => setFilterAccount(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white">
            <option value="">All</option>
            {accounts.map(a => <option key={a.id} value={a.accountNumber}>****{a.accountNumber}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[10px] text-gray-500 mb-1">Confirmed</label>
          <select value={filterConfirmed} onChange={e => setFilterConfirmed(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white">
            <option value="">All</option>
            <option value="confirmed">Confirmed</option>
            <option value="unconfirmed">Unconfirmed</option>
          </select>
        </div>
      </div>

      {/* Bulk action bar */}
      {selected.size > 0 && (
        <div className="bg-brand-950/40 border border-brand-800 rounded px-3 py-2 mb-3 flex items-center gap-3 flex-wrap text-xs">
          <span className="text-brand-300 font-medium">{selected.size} selected</span>
          <select value={bulkCategory} onChange={e => setBulkCategory(e.target.value)}
            className="bg-gray-800 border border-gray-700 rounded px-2 py-1 text-xs text-white">
            <option value="">Assign category…</option>
            {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
          </select>
          <button onClick={bulkAssignCategory} disabled={!bulkCategory}
            className="bg-brand-600 hover:bg-brand-500 disabled:opacity-40 text-white px-2 py-1 rounded">Apply</button>
          <button onClick={bulkConfirm} className="bg-green-700 hover:bg-green-600 text-white px-2 py-1 rounded">Confirm all</button>
          <button onClick={bulkDelete} className="bg-red-700 hover:bg-red-600 text-white px-2 py-1 rounded">Delete all</button>
          <button onClick={clearSelection} className="text-gray-400 hover:text-white px-2 py-1">Clear</button>
        </div>
      )}

      <p className="text-xs text-gray-500 mb-2">{filtered.length} transaction{filtered.length !== 1 ? 's' : ''}</p>

      <div className="overflow-x-auto border border-gray-800 rounded max-h-[32rem]">
        <table className="w-full text-xs">
          <thead className="bg-gray-800/60 text-gray-400 sticky top-0">
            <tr>
              <th className="text-center py-2 px-2 w-8">
                <input type="checkbox"
                  checked={filtered.length > 0 && filtered.every(t => selected.has(t.id))}
                  onChange={e => e.target.checked ? selectAllVisible() : clearSelection()} />
              </th>
              <th className="text-left py-2 px-2">Date</th>
              <th className="text-left py-2 px-2">Description</th>
              <th className="text-right py-2 px-2">Amount</th>
              <th className="text-left py-2 px-2">Category</th>
              <th className="text-left py-2 px-2">Account</th>
              <th className="text-center py-2 px-2 w-20">Confirmed</th>
              <th className="text-center py-2 px-2 w-20">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan="8" className="text-center py-6 text-gray-500">No transactions match these filters</td></tr>
            )}
            {filtered.map(tx => {
              const editing = editingId === tx.id
              return (
                <tr key={tx.id} className="border-t border-gray-800/60">
                  <td className="text-center py-1.5 px-2">
                    <input type="checkbox" checked={selected.has(tx.id)} onChange={() => toggleSelect(tx.id)} />
                  </td>
                  <td className="py-1.5 px-2 text-gray-300 font-mono">{tx.date}</td>
                  <td className="py-1.5 px-2 text-gray-300">
                    {editing ? (
                      <input type="text" value={editForm.description}
                        onChange={e => setEditForm(f => ({ ...f, description: e.target.value }))}
                        className="w-full bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-xs text-white" />
                    ) : tx.description}
                    {!editing && tx.notes && (
                      <span className="block text-[10px] text-gray-500 italic mt-0.5">{tx.notes}</span>
                    )}
                    {editing && (
                      <input type="text" value={editForm.notes}
                        onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))}
                        placeholder="Notes (optional)"
                        className="mt-1 w-full bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-xs text-gray-300" />
                    )}
                  </td>
                  <td className={`py-1.5 px-2 text-right font-medium ${tx.type === 'credit' ? 'text-green-400' : 'text-red-400'}`}>
                    {tx.type === 'credit' ? '+' : '-'}{formatR(tx.amount)}
                  </td>
                  <td className="py-1.5 px-2">
                    {editing ? (
                      <select value={editForm.category}
                        onChange={e => setEditForm(f => ({ ...f, category: e.target.value }))}
                        className="bg-gray-800 border border-gray-700 rounded px-2 py-0.5 text-xs text-white">
                        {categories.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                        <option value="Sales">Sales</option>
                      </select>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px]"
                        style={{ backgroundColor: `${categoryColour(tx.category)}20`, color: categoryColour(tx.category) }}>
                        <Tag size={9} /> {tx.category}
                      </span>
                    )}
                  </td>
                  <td className="py-1.5 px-2 text-gray-500 font-mono text-[10px]">****{tx.accountNumber}</td>
                  <td className="text-center py-1.5 px-2">
                    <button onClick={() => toggleConfirm(tx)}
                      className={tx.confirmed ? 'text-green-400 hover:text-green-300' : 'text-gray-600 hover:text-gray-400'}>
                      <CheckCircle size={14} />
                    </button>
                  </td>
                  <td className="py-1.5 px-2 text-center">
                    {editing ? (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => saveEdit(tx)} className="text-green-400 hover:text-green-300"><Check size={13} /></button>
                        <button onClick={() => setEditingId(null)} className="text-gray-500 hover:text-white"><X size={13} /></button>
                      </div>
                    ) : confirmDelete === tx.id ? (
                      <div className="flex items-center justify-center gap-1 text-[10px]">
                        <button onClick={() => handleDelete(tx.id)} className="text-red-400 hover:text-red-300">Yes</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-gray-500 hover:text-white">No</button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center gap-1">
                        <button onClick={() => startEdit(tx)} className="text-gray-500 hover:text-blue-400"><Edit2 size={12} /></button>
                        <button onClick={() => setConfirmDelete(tx.id)} className="text-gray-500 hover:text-red-400"><Trash2 size={12} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}

// ─── Section 4: Bank Charges Summary ──────────────────────────────

function BankChargesSummarySection({ charges, transactions }) {
  const [expanded, setExpanded] = useState(null)

  const months = useMemo(() => {
    const map = new Map()
    for (const c of charges) {
      const m = c.yearMonth || ''
      if (!map.has(m)) map.set(m, { yearMonth: m, service_fee: 0, cash_deposit_fee: 0, cash_handling_fee: 0, other_fee: 0 })
      const row = map.get(m)
      if (row[c.chargeType] != null) row[c.chargeType] += Number(c.amount || 0)
    }
    // Add accrued totals from transactions metadata isn't available
    // here — they're stored on charge records too via Phase 2/3 flow.
    return Array.from(map.values()).sort((a, b) => b.yearMonth.localeCompare(a.yearMonth))
  }, [charges])

  // Per-month accrued charge transactions (debits with cash deposit
  // descriptions captured at upload time).
  const monthlyAccrued = useMemo(() => {
    const map = new Map()
    for (const t of transactions) {
      const ym = (t.date || '').slice(0, 7)
      if (!ym) continue
      if (!map.has(ym)) map.set(ym, [])
    }
    return map
  }, [transactions])

  const totals = useMemo(() => {
    const t = { service_fee: 0, cash_deposit_fee: 0, cash_handling_fee: 0, other_fee: 0, accrued: 0 }
    for (const m of months) {
      t.service_fee += m.service_fee
      t.cash_deposit_fee += m.cash_deposit_fee
      t.cash_handling_fee += m.cash_handling_fee
      t.other_fee += m.other_fee
    }
    return t
  }, [months])

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-lg p-5">
      <h2 className="text-lg font-bold text-white flex items-center gap-2 mb-4">
        <Banknote size={18} className="text-brand-400" /> Bank Charges Summary
      </h2>

      {months.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">No bank charges recorded yet.</p>
      ) : (
        <div className="overflow-x-auto border border-gray-800 rounded">
          <table className="w-full text-xs">
            <thead className="bg-gray-800/60 text-gray-400">
              <tr>
                <th className="text-left py-2 px-3">Month</th>
                <th className="text-right py-2 px-3">Service Fees</th>
                <th className="text-right py-2 px-3">Cash Deposit</th>
                <th className="text-right py-2 px-3">Cash Handling</th>
                <th className="text-right py-2 px-3">Other</th>
                <th className="text-right py-2 px-3">Total</th>
              </tr>
            </thead>
            <tbody>
              {months.map(m => {
                const total = m.service_fee + m.cash_deposit_fee + m.cash_handling_fee + m.other_fee
                const isOpen = expanded === m.yearMonth
                return (
                  <>
                    <tr key={m.yearMonth} className="border-t border-gray-800/60 hover:bg-gray-800/30 cursor-pointer"
                      onClick={() => setExpanded(isOpen ? null : m.yearMonth)}>
                      <td className="py-1.5 px-3 text-gray-300 font-mono flex items-center gap-1">
                        {isOpen ? <ChevronDown size={11} /> : <ChevronRight size={11} />}
                        {m.yearMonth}
                      </td>
                      <td className="py-1.5 px-3 text-right text-gray-300">{formatR(m.service_fee)}</td>
                      <td className="py-1.5 px-3 text-right text-gray-300">{formatR(m.cash_deposit_fee)}</td>
                      <td className="py-1.5 px-3 text-right text-gray-300">{formatR(m.cash_handling_fee)}</td>
                      <td className="py-1.5 px-3 text-right text-gray-300">{formatR(m.other_fee)}</td>
                      <td className="py-1.5 px-3 text-right text-amber-300 font-medium">{formatR(total)}</td>
                    </tr>
                    {isOpen && (
                      <tr className="bg-gray-900/60 border-t border-gray-800/60">
                        <td colSpan="6" className="px-6 py-2 text-[11px] text-gray-500">
                          Per-fee breakdown for {m.yearMonth}: Service {formatR(m.service_fee)}, Cash Deposit {formatR(m.cash_deposit_fee)}, Cash Handling {formatR(m.cash_handling_fee)}, Other {formatR(m.other_fee)}
                        </td>
                      </tr>
                    )}
                  </>
                )
              })}
              <tr className="border-t border-gray-700 bg-gray-800/40 font-medium">
                <td className="py-2 px-3 text-gray-300">Totals</td>
                <td className="py-2 px-3 text-right text-gray-200">{formatR(totals.service_fee)}</td>
                <td className="py-2 px-3 text-right text-gray-200">{formatR(totals.cash_deposit_fee)}</td>
                <td className="py-2 px-3 text-right text-gray-200">{formatR(totals.cash_handling_fee)}</td>
                <td className="py-2 px-3 text-right text-gray-200">{formatR(totals.other_fee)}</td>
                <td className="py-2 px-3 text-right text-amber-300">
                  {formatR(totals.service_fee + totals.cash_deposit_fee + totals.cash_handling_fee + totals.other_fee)}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

// ─── Section 5: Categories panel ──────────────────────────────────

function CategoriesPanel({ departmentId, categories, transactions, addToast, onChange }) {
  const [open, setOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [adding, setAdding] = useState(false)
  const [form, setForm] = useState({ name: '', type: 'expense', colour: COLOUR_SWATCHES[0], keywords: '' })
  const [confirmDelete, setConfirmDelete] = useState(null)

  const usageByCategory = useMemo(() => {
    const m = new Map()
    for (const t of transactions) {
      m.set(t.category, (m.get(t.category) || 0) + 1)
    }
    return m
  }, [transactions])

  function reset() {
    setForm({ name: '', type: 'expense', colour: COLOUR_SWATCHES[0], keywords: '' })
    setEditingId(null)
    setAdding(false)
  }

  function startEdit(cat) {
    setForm({
      name: cat.name,
      type: cat.type || 'expense',
      colour: cat.colour || COLOUR_SWATCHES[0],
      keywords: (cat.keywords || []).join(', '),
    })
    setEditingId(cat.id)
    setAdding(false)
  }

  async function handleSave() {
    if (!form.name.trim()) {
      addToast('Name is required', 'error')
      return
    }
    try {
      const keywords = form.keywords.split(',').map(s => s.trim()).filter(Boolean)
      await saveExpenseCategory({
        departmentId,
        name: form.name.trim(),
        type: form.type,
        colour: form.colour,
        keywords,
      })
      addToast(editingId ? 'Category updated' : 'Category added', 'success')
      reset()
      onChange()
    } catch (err) {
      addToast(`Failed to save: ${err.message}`, 'error')
    }
  }

  async function handleDelete(cat) {
    const usage = usageByCategory.get(cat.name) || 0
    if (usage > 0) {
      addToast(`Cannot delete — ${usage} transaction${usage !== 1 ? 's' : ''} use this category.`, 'error')
      setConfirmDelete(null)
      return
    }
    try {
      await deleteExpenseCategory(cat.id, departmentId)
      addToast('Category deleted', 'success')
      setConfirmDelete(null)
      onChange()
    } catch (err) {
      addToast(`Failed to delete: ${err.message}`, 'error')
    }
  }

  return (
    <section className="bg-gray-900 border border-gray-800 rounded-lg">
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <h2 className="text-sm font-semibold text-white flex items-center gap-2">
          <Tag size={14} className="text-brand-400" /> Manage Expense Categories
          <span className="text-xs text-gray-500 font-normal">({categories.length})</span>
        </h2>
        {open ? <ChevronDown size={14} className="text-gray-500" /> : <ChevronRight size={14} className="text-gray-500" />}
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-3">
          {(adding || editingId) && (
            <div className="bg-gray-800/50 border border-gray-700 rounded p-3 space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Name</label>
                  <input type="text" value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white" />
                </div>
                <div>
                  <label className="block text-[10px] text-gray-500 mb-1">Type</label>
                  <div className="flex gap-1">
                    {[
                      { key: 'expense', label: 'Expense' },
                      { key: 'bank_charge', label: 'Bank Charge' },
                    ].map(t => (
                      <button key={t.key}
                        onClick={() => setForm(f => ({ ...f, type: t.key }))}
                        className={`flex-1 px-2 py-1 text-xs rounded border ${
                          form.type === t.key ? 'bg-brand-600 border-brand-500 text-white' : 'bg-gray-900 border-gray-700 text-gray-400'
                        }`}>
                        {t.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Colour</label>
                <div className="flex gap-1">
                  {COLOUR_SWATCHES.map(c => (
                    <button key={c}
                      onClick={() => setForm(f => ({ ...f, colour: c }))}
                      className={`w-6 h-6 rounded ${form.colour === c ? 'ring-2 ring-white ring-offset-2 ring-offset-gray-800' : ''}`}
                      style={{ backgroundColor: c }} />
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-[10px] text-gray-500 mb-1">Keywords (comma-separated)</label>
                <input type="text" value={form.keywords}
                  onChange={e => setForm(f => ({ ...f, keywords: e.target.value }))}
                  placeholder="e.g. eskom, electricity, water"
                  className="w-full bg-gray-900 border border-gray-700 rounded px-2 py-1 text-xs text-white" />
              </div>
              <div className="flex justify-end gap-2">
                <button onClick={reset} className="px-3 py-1 text-xs text-gray-400 hover:text-white">Cancel</button>
                <button onClick={handleSave} className="bg-brand-600 hover:bg-brand-500 text-white px-3 py-1 rounded text-xs">Save</button>
              </div>
            </div>
          )}

          {!adding && !editingId && (
            <button onClick={() => setAdding(true)} className="flex items-center gap-1 text-xs text-brand-400 hover:text-brand-300">
              <Plus size={11} /> Add Category
            </button>
          )}

          <div className="border border-gray-800 rounded">
            {categories.length === 0 ? (
              <p className="p-3 text-xs text-gray-500 text-center">No categories yet.</p>
            ) : (
              categories.map(cat => {
                const usage = usageByCategory.get(cat.name) || 0
                return (
                  <div key={cat.id} className="flex items-center gap-3 px-3 py-2 border-t border-gray-800/60 first:border-t-0">
                    <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: cat.colour || '#9ca3af' }} />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-white font-medium truncate">{cat.name}</p>
                      <p className="text-[10px] text-gray-500">
                        {cat.type === 'bank_charge' ? 'Bank Charge' : 'Expense'} · {(cat.keywords || []).length} keyword{(cat.keywords || []).length !== 1 ? 's' : ''} · {usage} use{usage !== 1 ? 's' : ''}
                      </p>
                    </div>
                    {confirmDelete === cat.id ? (
                      <div className="flex items-center gap-1">
                        <button onClick={() => handleDelete(cat)} className="text-[10px] bg-red-700 hover:bg-red-600 text-white px-2 py-0.5 rounded">Delete</button>
                        <button onClick={() => setConfirmDelete(null)} className="text-[10px] text-gray-400 hover:text-white">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1">
                        <button onClick={() => startEdit(cat)} className="text-gray-500 hover:text-blue-400"><Edit2 size={11} /></button>
                        <button onClick={() => setConfirmDelete(cat.id)} className="text-gray-500 hover:text-red-400"><Trash2 size={11} /></button>
                      </div>
                    )}
                  </div>
                )
              })
            )}
          </div>
        </div>
      )}
    </section>
  )
}
